type CachedService<T extends object> = T & {
  cachedValues: Map<string, any>;
};

const cachedServices = new Map<object, CachedService<any>>();

export const clearServiceCache = (service: object) => {
  if (cachedServices.has(service)) {
    const fnCacheMap = cachedServices.get(service);
    fnCacheMap.cachedValues.clear();
    cachedServices.delete(service);
  }
};

export const clearAllServicesCache = () => {
  cachedServices.forEach((cachedService: CachedService<any>) => {
    cachedService.cachedValues.clear();
  });
  cachedServices.clear();
};

/** Retorna uma instância do Service  */
export const cachedService = <T extends object>(
  service: T
): CachedService<T> => {
  if (cachedServices.has(service)) {
    return cachedServices.get(service);
  }
  const cachedService = new Proxy(service, {
    get(target, propKey) {
      const origMethod = target[propKey];

      if (typeof origMethod !== 'function') return origMethod;

      if (!cachedService.cachedValues) {
        cachedService.cachedValues = new Map();
      }

      const fnCacheMap = cachedService.cachedValues;

      return function (...args: any[]) {
        const cacheKey = JSON.stringify(args);

        if (fnCacheMap.has(cacheKey)) {
          return fnCacheMap.get(cacheKey);
        }

        const result = origMethod.apply(service, args);

        fnCacheMap.set(cacheKey, result);

        if (result instanceof Promise) {
          result.catch(e => {
            if (fnCacheMap.has(cacheKey)) {
              fnCacheMap.delete(cacheKey);
            }
            throw e;
          });
        }

        return result;
      };
    }
  }) as CachedService<T>;

  cachedService.cachedValues = new Map();
  cachedServices.set(service, cachedService);
  return cachedService;
};
